// kpi.repo — data KPI: karyawan + tiket (untuk aktual otomatis) + hitung aktual per-orang.
import { supabase } from '@core/supabase';
import { fetchAllChunked, fetchAllPaged } from '@core/paging';
import { rowToTicket, fetchRounds, MONEY_COLS, type TicketRow } from '@core/ticketMap';
import { omzetKomisi, profitKomisi, piutangTiket } from '@domain/money';
import { statusDurations, slaCompliance, financeVerifyCompliance, type LogEntry } from '@domain/sla';
import { attEffKat, hariKerjaEfektif, hariTepatUntukKpi } from '@domain/attendance';
import { isoOf } from '@domain/format';
import { absensi } from '@core/settings';
import { listAttendance, listHolidays, listLeaves, listOutletsGeo, type RekapRow, type Holiday, type Leave, type OutletGeo } from '@modules/absensi/absensi.repo';
import type { Ticket } from '@core/types';

export const fetchAttendance = (): Promise<RekapRow[]> => listAttendance();

/** Konteks untuk hitung HARI KERJA EFEKTIF (fix F1): hari libur + cuti + hari-kerja per cabang. */
export interface AbsCtx { holidays: Holiday[]; leaves: Leave[]; outlets: OutletGeo[] }
export async function fetchAbsCtx(): Promise<AbsCtx> {
  const [holidays, leaves, outlets] = await Promise.all([listHolidays(), listLeaves(), listOutletsGeo()]);
  return { holidays, leaves, outlets };
}
/** Hari kerja efektif karyawan pada periode = hari kerja cabangnya − libur − cuti approved miliknya.
 *  Bila outlet/hari-kerja TAK diketahui (ctx gagal-fetch atau karyawan lintas-cabang) → kembalikan 0
 *  agar autoActuals PAKAI PERILAKU LAMA (denominator = hari ber-record). Tanpa guard ini, ctx kosong
 *  akan memfabrikasi alpha untuk semua orang (fallback [1..6] menghitung hari kerja walau data absen). */
export function attExpectedFor(emp: KpiEmp, ctx: AbsCtx, y: number, m: number, q: number, mode: 'M' | 'Q' | 'Y'): number {
  const o = ctx.outlets.find((x) => x.kode === emp.outlet);
  if (!o) return 0;
  const hol = ctx.holidays.filter((h) => !h.outlet || h.outlet === emp.outlet).map((h) => h.tgl);
  const cuti = ctx.leaves.filter((l) => l.emp_id === emp.id && l.status === 'approved').map((l) => ({ from: l.from_tgl, to: l.to_tgl }));
  return hariKerjaEfektif(o.hari_kerja, hol, cuti, y, m, q, mode, isoOf(new Date())); // asOf = hari ini → hari depan tak divonis alpha
}

/** ticket_logs untuk daftar tiket tertentu (Kepatuhan SLA). Map by ticket_no, kronologis.
 *  DIBATASI `.in('ticket_no', …)` + chunk (hindari full-table fetch & silent-truncation db-max-rows). */
export async function fetchTicketLogs(nos: string[]): Promise<Map<string, LogEntry[]>> {
  const map = new Map<string, LogEntry[]>();
  const uniq = [...new Set(nos)];
  if (!uniq.length) return map;
  const rows = await fetchAllChunked<{ ticket_no: string; status: string; catatan: string | null; at: string }>(uniq,
    (bagian, a, b) => supabase().from('ticket_logs').select('ticket_no,status,catatan,at')
      .in('ticket_no', bagian).order('ticket_no').order('at').range(a, b));
  for (const r of rows) {
    const arr = map.get(r.ticket_no) ?? []; arr.push({ status: r.status, at: r.at, catatan: r.catatan }); map.set(r.ticket_no, arr);
  }
  return map;
}

/** Opsi aktual SLA: logs + config (di-inject dari view; tanpa ini kecepatan tetap dihitung, kepatuhan_sla absen). */
export interface SlaOpts { logsByTicket?: Map<string, LogEntry[]>; slaCfg?: Record<string, number>; verifikasiBayarMin?: number }
const OP_SLA_STATUSES: Record<string, string[]> = {
  Teknisi: ['Diagnosa', 'Dikerjakan'], CS: ['Menunggu Persetujuan'], SPV: ['Diterima'],
  Manager: ['Diterima', 'Diagnosa', 'Menunggu Persetujuan', 'Dikerjakan'],
};

export interface KpiEmp { id: number; nama: string; role: string; outlet: string | null }
export async function listKpiEmployees(): Promise<KpiEmp[]> {
  const { data, error } = await supabase().from('employees').select('id,nama,role,outlet')
    .eq('aktif', true).in('role', ['Teknisi', 'CS', 'SPV', 'Manager', 'Admin Finance']).order('nama');
  if (error) throw new Error(error.message);
  return (data ?? []) as KpiEmp[];
}

interface Meta { teknisi: string | null; penerima: string | null; servis_ulang: boolean | null; rating: number | null; survey_done: boolean | null }
export interface KpiTicket { t: Ticket; teknisi: string | null; penerima: string | null; servisUlang: boolean; rating: number | null; surveyDone: boolean }

export async function fetchKpiTickets(): Promise<KpiTicket[]> {
  const rows = await fetchAllPaged<TicketRow & Meta>((dari, sampai) => supabase().from('tickets')
    .select(`${MONEY_COLS},penerima,servis_ulang,rating,survey_done`).eq('archived', false)
    .order('no').range(dari, sampai));
  const rounds = await fetchRounds(rows.map((r) => r.no));
  return rows.map((r) => ({ t: rowToTicket(r, rounds.get(r.no) ?? []), teknisi: r.teknisi, penerima: r.penerima, servisUlang: r.servis_ulang ?? false, rating: r.rating, surveyDone: r.survey_done ?? false }));
}

/** Subset tiket relevan utk seorang karyawan (per peran). */
export function subsetFor(emp: KpiEmp, all: KpiTicket[]): KpiTicket[] {
  if (emp.role === 'Teknisi') return all.filter((x) => x.teknisi === emp.nama);
  if (emp.role === 'CS') return all.filter((x) => x.penerima === emp.nama);
  if (emp.role === 'SPV') return all.filter((x) => x.t.outlet === emp.outlet);
  return all; // Manager (tim) / Admin Finance (mayoritas manual)
}

/** Aktual OTOMATIS per-KRA (dari tiket+survei+absensi). KRA manual (kecepatan/penagihan/disiplin) tetap "—".
 *  `inPeriod` (opsional): filter periode — tiket by tgl-ambil/terima (basis kas), absensi by tgl. Tanpa param = semua-waktu. */
/** attExpected = HARI KERJA EFEKTIF emp di periode (hari kerja − libur − cuti). Bila diberikan (>0),
 *  hari kerja tanpa record dihitung ALPHA & kehadiran% dibagi hari kerja (fix F1 no-show). Bila tak
 *  diberikan → perilaku lama (denominator = hari ber-record) demi kompatibilitas caller yang belum diwire. */
export function autoActuals(emp: KpiEmp, all: KpiTicket[], att: RekapRow[] = [], inPeriod?: (iso: string | null | undefined) => boolean, sla?: SlaOpts, attExpected?: number): Record<string, number> {
  const match = inPeriod ?? ((): boolean => true);
  // Basis periode BEDA: metrik OPERASIONAL (count/rasio) by tgl-TERIMA (v18 periodMatchTgt=tglISO);
  // metrik UANG by tgl-AMBIL (basis-kas, keputusan Apo). Absensi by tgl absensi.
  const recSub = subsetFor(emp, all.filter((x) => match(x.t.tglISO)));       // operasional
  // Uang KPI per tanggal AMBIL saja (tanpa cadangan tgl terima): DP unit yang belum diambil
  // bukan omzet teknisi, dan menghitungnya membuat KPI naik sebelum pekerjaannya selesai.
  const cashSub = subsetFor(emp, all.filter((x) => match(x.t.tglAmbilISO ?? null)));
  const attP = att.filter((a) => match(a.tgl));
  const myAtt = attP.filter((a) => a.emp_id === emp.id);
  const cnt = (k: string): number => myAtt.filter((a) => attEffKat(a) === k).length; // kategori EFEKTIF (hormati koreksi/approval)
  // "Tepat" untuk KPI lebih ketat dari kategori di menu Absensi: hari yang istirahatnya LEWAT JATAH
  // tak ikut dihitung, sehingga % Kehadiran turun (aturan Apo 5 Agu 2026). Telat/½/alpha tak berubah
  // — hari yang sudah tidak tepat tak dihukum dua kali. Jatah 0 = tak dibatasi → tak ada efek.
  const jatahIstirahat = absensi().istirahatMenit;
  const ontime = myAtt.filter((a) => hariTepatUntukKpi(a, jatahIstirahat)).length;
  const telat = cnt('telat'), setengah = cnt('setengah'), alphaRec = cnt('alpha');
  const hadirRecord = cnt('ontime') + telat + setengah + alphaRec;   // jumlah hari BER-RECORD (bukan yang diperketat) → denominator & alpha tak bergeser
  // Denominator kehadiran = hari kerja efektif (attExpected) bila diwire; hari kerja tanpa record = ALPHA.
  const expected = attExpected != null && attExpected > 0 ? attExpected : hadirRecord;
  const noShow = Math.max(0, expected - hadirRecord);
  const alpha = alphaRec + noShow;
  const jadi = recSub.filter((x) => x.t.hasil === 'jadi');
  const failed = recSub.filter((x) => x.t.hasil === 'tidakbisa' || x.t.hasil === 'batal');
  const surveyed = jadi.filter((x) => x.surveyDone && x.rating);
  const ratingAvg = surveyed.length ? surveyed.reduce((a, x) => a + (x.rating ?? 0), 0) / surveyed.length : 0;
  const denom = jadi.length + failed.length;
  // kecepatan (v18 auto): rata² hari tgl-ambil − tgl-terima atas tiket yang punya kedua tanggal.
  const kecDone = recSub.filter((x) => x.t.tglAmbilISO && x.t.tglISO);
  const kecepatan = kecDone.length
    ? kecDone.reduce((a, x) => a + Math.max(0, (new Date(x.t.tglAmbilISO as string).getTime() - new Date(x.t.tglISO as string).getTime()) / 86400000), 0) / kecDone.length
    : 0;
  const out: Record<string, number> = {
    produktivitas: jadi.length,
    kualitas: denom ? (jadi.length / denom) * 100 : 0,
    failed: denom ? (failed.length / denom) * 100 : 0, // % gagal (v0.18), bukan jumlah mentah
    rework: recSub.filter((x) => x.servisUlang).length,
    // omzetKomisi/profitKomisi: KRA ini menilai ORANG, bukan toko. Klaim garansi memindahkan
    // tanggal ambil ke bulan klaim, jadi omzetTiket akan menghitung uang sesi lama sekali lagi di
    // KPI bulan itu — dan klaim yang gagal harus MENGURANGI omzet teknisi (aturan Apo 31 Agu).
    omzet: cashSub.reduce((a, x) => a + omzetKomisi(x.t), 0),
    profit: cashSub.reduce((a, x) => a + profitKomisi(x.t), 0),
    piutang: cashSub.reduce((a, x) => a + piutangTiket(x.t), 0),
    csat: ratingAvg,
    response_rate: jadi.length ? (jadi.filter((x) => x.surveyDone).length / jadi.length) * 100 : 0,
    intake: emp.role === 'CS' ? all.filter((x) => match(x.t.tglISO) && x.penerima === emp.nama).length : jadi.length,
    // expected=0 (periode seluruhnya di masa depan / tak ada hari kerja due) → 100 (tak ada yang perlu dihadiri), selaras v0.18.
    kehadiran: expected > 0 ? (ontime / expected) * 100 : 100,
    keterlambatan: telat + setengah,
    alpha,
    kecepatan,
  };
  // Kepatuhan SLA (%) — hanya bila logs+cfg di-inject & ada fase terukur (else absen → tampil "—").
  if (sla?.logsByTicket && sla.slaCfg) {
    const logs = sla.logsByTicket;
    if (emp.role === 'Admin Finance') {
      const fin = all.filter((x) => x.t.outlet === emp.outlet && match(x.t.tglISO));
      let within = 0, total = 0;
      for (const x of fin) { const r = financeVerifyCompliance(logs.get(x.t.no) ?? [], sla.verifikasiBayarMin ?? 0); within += r.within; total += r.total; }
      if (total) out.kepatuhan_sla = (within / total) * 100;
    } else {
      const st = OP_SLA_STATUSES[emp.role];
      if (st) {
        const phases = recSub.flatMap((x) => statusDurations(logs.get(x.t.no) ?? []));
        const r = slaCompliance(phases, st, sla.slaCfg);
        if (r.total) out.kepatuhan_sla = (r.within / r.total) * 100;
      }
    }
  }
  return out;
}
